import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useCallback, useState } from 'react'

export interface OpenCaseState {
  status: 'idle' | 'running' | 'done' | 'error'
  caseId?: string
  breachTs?: string
  error?: string
}

/**
 * Open a case at a manually picked out-of-band point via fim-service
 * (`POST /api/v1/sites/{site}/sentinel/{entryId}/case`). The service re-scores
 * around the timestamp, rejects in-band clicks (surfaced as `error`), and opens
 * the SAME case entry the monitor opens — from there the existing "Run RCA"
 * flow takes over. Idempotent on (sentinel, breach_ts): clicking the same point
 * twice reuses the one case.
 */
export const useOpenSentinelCase = () => {
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()
  const [state, setState] = useState<OpenCaseState>({ status: 'idle' })

  const open = useCallback(
    async (
      entryId: string,
      site: string,
      breachTs: string
    ): Promise<boolean> => {
      setState({ status: 'running' })
      try {
        const res = await authorizedAxios.post(
          `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(
            site
          )}/sentinel/${encodeURIComponent(entryId)}/case`,
          { breach_ts: breachTs }
        )
        const data = res.data as { case_id?: string; breach_ts?: string }
        setState({
          status: 'done',
          caseId: data.case_id,
          breachTs: data.breach_ts,
        })
        return true
      } catch (err) {
        // The service returns 400 with a clear detail for an in-band click.
        const detail = (err as { response?: { data?: { detail?: string } } })
          ?.response?.data?.detail
        setState({
          status: 'error',
          error: detail ?? (err instanceof Error ? err.message : String(err)),
        })
        return false
      }
    },
    [FIM_SERVICE_URL, authorizedAxios]
  )

  const reset = useCallback(() => setState({ status: 'idle' }), [])

  return { state, open, reset }
}
